import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAuthHeaders } from '../utils/api';
import CopyInviteLinkButton from './CopyInviteLinkButton';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function ClubSwitcher({ onClubSwitch }) {
  const { token, user, isSuperAdmin, impersonatedClubCode, switchClub, getEffectiveClubCode } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  
  // New club creation state
  const [showCreateClub, setShowCreateClub] = useState(false);
  const [newClubCode, setNewClubCode] = useState('');
  const [newClubDisplayName, setNewClubDisplayName] = useState('');
  const [newClubPrimaryColor, setNewClubPrimaryColor] = useState('#1e3a8a');
  const [adminEmail, setAdminEmail] = useState('');
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState('');
  const [createdInviteUrl, setCreatedInviteUrl] = useState('');

  useEffect(() => {
    if (isSuperAdmin()) {
      fetchClubs();
    } else {
      setClubs([]);
      setLoading(false);
    }
  }, [token]);

  const fetchClubs = async () => {
    if (!isSuperAdmin()) {
      setClubs([]);
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/teams/all-clubs`, {
        headers: getAuthHeaders(token)
      });
      if (response.ok) {
        const data = await response.json();
        setClubs(data.clubs || []);
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClub = async () => {
    if (!newClubCode.trim()) {
      setCreateMessage('Club code is required');
      return;
    }
    if (!adminEmail.trim()) {
      setCreateMessage('Admin email is required');
      return;
    }

    setCreating(true);
    setCreateMessage('');
    setCreatedInviteUrl('');

    try {
      // Step 1: Create the club
      const createResponse = await fetch(`${API_URL}/api/teams/create-club`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          clubCode: newClubCode.trim().toUpperCase(),
          displayName: newClubDisplayName.trim() || newClubCode.trim().toUpperCase(),
          primaryColor: newClubPrimaryColor
        })
      });

      const createData = await createResponse.json();

      if (!createResponse.ok) {
        setCreateMessage(createData.error || 'Failed to create club');
        setCreating(false);
        return;
      }

      // Step 2: Send admin invitation for the new club
      // We need to temporarily set the X-Club-Code header to the new club
      const inviteResponse = await fetch(`${API_URL}/api/invitations`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(token),
          'X-Club-Code': createData.club.clubCode // Override to new club
        },
        body: JSON.stringify({
          email: adminEmail.trim(),
          role: 'admin'
        })
      });

      const inviteData = await inviteResponse.json();

      fetchClubs();

      if (inviteResponse.ok) {
        const inviteUrl = inviteData.invitation?.inviteUrl || '';
        setCreatedInviteUrl(inviteUrl);
        setCreateMessage(
          inviteData.emailSent
            ? `Club "${createData.club.clubCode}" created. Admin invitation sent to ${adminEmail}.`
            : `Club "${createData.club.clubCode}" created. Copy the admin invite link to share it.`
        );
        setNewClubCode('');
        setNewClubDisplayName('');
        setNewClubPrimaryColor('#1e3a8a');
        setAdminEmail('');
      } else {
        setCreatedInviteUrl('');
        setCreateMessage(`Club created but invitation failed: ${inviteData.error}`);
      }
    } catch (error) {
      console.error('Error creating club:', error);
      setCreateMessage('Error creating club');
    } finally {
      setCreating(false);
    }
  };

  const handleSelectClub = (clubCode) => {
    setIsOpen(false);
    // Set localStorage directly before reload to avoid race conditions
    localStorage.setItem('impersonatedClubCode', clubCode);
    // Small delay to ensure localStorage is flushed
    setTimeout(() => {
      window.location.reload();
    }, 50);
  };

  const handleClearImpersonation = () => {
    setIsOpen(false);
    // Clear localStorage before reload
    localStorage.removeItem('impersonatedClubCode');
    setTimeout(() => {
      window.location.reload();
    }, 50);
  };

  if (!isSuperAdmin()) return null;

  const effectiveClubCode = getEffectiveClubCode();
  const isImpersonating = impersonatedClubCode && impersonatedClubCode !== user?.clubCode;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
          isImpersonating
            ? 'bg-purple-600 text-white hover:bg-purple-700'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
        <span className="hidden md:inline">
          {isImpersonating ? `Viewing: ${effectiveClubCode}` : 'Switch Club'}
        </span>
        <span className="md:hidden">
          {isImpersonating ? effectiveClubCode : '⇄'}
        </span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl overflow-hidden z-50 border border-gray-200">
            <div className="px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white">
              <div className="text-sm font-semibold">🔮 Superadmin Mode</div>
              <div className="text-xs text-purple-200">Switch to view any club's environment</div>
            </div>
            
            {isImpersonating && (
              <div className="px-4 py-2 bg-purple-50 border-b border-purple-100">
                <button
                  onClick={handleClearImpersonation}
                  className="text-sm text-purple-700 font-medium hover:text-purple-900 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Return to my club ({user?.clubCode})
                </button>
              </div>
            )}

            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-2"></div>
                  Loading clubs...
                </div>
              ) : clubs.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  No clubs found
                </div>
              ) : (
                <div className="py-2">
                  {clubs.map((club) => (
                    <button
                      key={club.clubCode}
                      onClick={() => handleSelectClub(club.clubCode)}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                        effectiveClubCode === club.clubCode
                          ? 'bg-purple-100 text-purple-900'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Club Color/Logo Indicator */}
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: club.primaryColor || '#1e3a8a' }}
                      >
                        {club.clubCode.substring(0, 2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {club.displayName || club.clubCode}
                        </div>
                        <div className="text-xs text-gray-500">
                          {club.clubCode} • {club.userCount} user{club.userCount !== 1 ? 's' : ''}
                        </div>
                      </div>
                      {effectiveClubCode === club.clubCode && (
                        <svg className="w-5 h-5 text-purple-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Create New Club Section */}
            <div className="border-t border-gray-200">
              {!showCreateClub ? (
                <button
                  onClick={() => setShowCreateClub(true)}
                  className="w-full px-4 py-3 text-left text-sm font-medium text-purple-700 hover:bg-purple-50 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create New Club
                </button>
              ) : (
                <div className="p-4 bg-purple-50">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-sm font-semibold text-purple-900">Create New Club</h4>
                    <button 
                      onClick={() => { setShowCreateClub(false); setCreateMessage(''); setCreatedInviteUrl(''); }}
                      className="text-purple-600 hover:text-purple-800"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Club Code *</label>
                      <input
                        type="text"
                        value={newClubCode}
                        onChange={(e) => setNewClubCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                        placeholder="e.g., STANFORD"
                        maxLength={20}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Display Name</label>
                      <input
                        type="text"
                        value={newClubDisplayName}
                        onChange={(e) => setNewClubDisplayName(e.target.value)}
                        placeholder="e.g., Stanford Rowing"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Primary Color</label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={newClubPrimaryColor}
                          onChange={(e) => setNewClubPrimaryColor(e.target.value)}
                          className="w-10 h-10 rounded cursor-pointer border border-gray-300"
                        />
                        <input
                          type="text"
                          value={newClubPrimaryColor}
                          onChange={(e) => setNewClubPrimaryColor(e.target.value)}
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                    
                    <div className="border-t border-purple-200 pt-3 mt-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Admin Email *</label>
                      <input
                        type="email"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        placeholder="admin@example.com"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">An admin invitation will be sent to this email</p>
                    </div>
                    
                    {createMessage && (
                      <div className={`text-sm p-2 rounded ${createMessage.includes('created') && !createMessage.includes('failed') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        <p>{createMessage}</p>
                        {createdInviteUrl && (
                          <div className="mt-2">
                            <p className="text-xs break-all">{createdInviteUrl}</p>
                            <CopyInviteLinkButton url={createdInviteUrl} className="mt-1 text-green-800 hover:text-green-900 text-xs font-semibold" />
                          </div>
                        )}
                      </div>
                    )}
                    
                    <button
                      onClick={handleCreateClub}
                      disabled={creating || !newClubCode.trim() || !adminEmail.trim()}
                      className="w-full py-2 px-4 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creating ? 'Creating...' : 'Create Club & Send Invite'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              💡 Switching clubs lets you view their data and settings
            </div>
          </div>
        </>
      )}
    </div>
  );
}

